import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

const sections = [
  {
    number: '1',
    title: 'Definitions',
    items: [
      { label: 'Available Balance', text: 'A ledger-based allocation representing a claim against Thunderbolt for funds received on your behalf. Your Available Balance is not a bank deposit, is not held in a segregated account, and is not insured by the FDIC or any government agency.' },
      { label: 'Earning Balance', text: 'Promotional reward credits allocated in Bitcoin (BTC) and displayed as a USD-equivalent value based on the current market price of Bitcoin. Earning balances are not deposits, securities, or investment products.' },
      { label: 'Services', text: "Thunderbolt's website, mobile application, payment tools, payment links, invoicing tools, and related features." },
      { label: 'Guest Checkout', text: 'The ability to make a payment without creating a Thunderbolt account.' },
    ]
  },
  {
    number: '2',
    title: 'Overview of Services',
    content: "Thunderbolt is a payments facilitation platform that enables users to accept payments from customers via card or bank transfer. Incoming payments are processed by licensed third-party payment processors and received into Thunderbolt's corporate treasury, with a corresponding balance allocated to the merchant's account in Thunderbolt's internal ledger. Thunderbolt is not a bank, money transmitter, cryptocurrency exchange, investment adviser, or custodian of any kind."
  },
  {
    number: '3',
    title: 'Payment Processing',
    content: 'All payment transactions are processed by licensed third-party payment processors. Thunderbolt does not operate as a financial institution or bank. Payment processing is subject to the terms of the applicable third-party processor.'
  },
  {
    number: '4',
    title: 'Nature of Balances',
    content: "When a payment is received on your behalf, Thunderbolt receives the funds into its corporate treasury and allocates a corresponding balance to your account in its internal ledger. Your Available Balance represents a claim against Thunderbolt for the allocated amount.",
    items: [
      { label: 'Not a bank deposit', text: 'Your balance is not held at a bank or financial institution in your name.' },
      { label: 'Not FDIC insured', text: 'Balances are not insured by the Federal Deposit Insurance Corporation or any government agency.' },
      { label: 'Not segregated', text: "Funds are held in Thunderbolt's corporate treasury and are not held in accounts segregated by user." },
      { label: 'Not a custodial account', text: 'Thunderbolt is not a licensed custodian. Your balance is a contractual claim against Thunderbolt, not a property interest in specific funds.' },
    ],
    footer: 'Thunderbolt maintains sufficient treasury assets to satisfy user balance claims. You may request a payout to your connected bank account at any time, subject to these Terms.'
  },
  {
    number: '5',
    title: 'Guest Payments',
    content: 'Users may make payments without creating an account ("Guest Checkout"). Guest users may be required to provide payment information directly to the payment processor. We do not store full payment card details.'
  },
  {
    number: '6',
    title: 'Accounts',
    content: 'Users who create an account are responsible for maintaining account security, all activity under their account, and providing accurate information. We may suspend or terminate accounts for misuse or fraud.'
  },
  {
    number: '7',
    title: 'Fees',
    content: 'We may charge fees for use of the Services, including transaction-based fees. All applicable fees will be disclosed prior to transaction completion. We reserve the right to modify fees at any time with reasonable notice.'
  },
  {
    number: '8',
    title: 'Rewards Program ("Earning")',
    content: "Thunderbolt may offer a promotional rewards program (\"Earning\") that allocates Bitcoin (BTC) rewards to users based on platform activity. Rewards are funded entirely at the Company's discretion from its internal treasury.",
    items: [
      { label: 'Allocated in BTC', text: "All rewards are allocated in Bitcoin (BTC), held in Thunderbolt's corporate treasury with a share allocated to your Earning balance, and displayed as a USD-equivalent value based on the current market price of Bitcoin." },
      { label: 'Value fluctuates', text: 'Because rewards are denominated in Bitcoin, their USD-equivalent value will rise and fall with the market price of Bitcoin. You may receive more or less than the value displayed at the time a reward was credited.' },
      { label: 'Not a guaranteed return', text: 'Rewards are not earned income, interest, or a guaranteed return. Bitcoin is a highly volatile asset and may lose significant value.' },
      { label: 'Not an investment', text: 'Participation in the rewards program does not constitute an investment, and Thunderbolt is not an investment adviser.' },
      { label: 'Not securities or deposits', text: 'Rewards do not represent securities, bank deposits, or investment products of any kind.' },
      { label: 'Redemption', text: 'Rewards may be redeemed subject to program rules set by Thunderbolt at its discretion.' },
    ],
    footer: 'Thunderbolt may modify, suspend, or terminate the rewards program at any time without notice.'
  },
  {
    number: '9',
    title: 'Internal Treasury & Asset Management',
    content: 'To fund operations and the rewards program, Thunderbolt maintains an internal corporate treasury. This treasury holds assets including US Dollar-denominated stablecoins (USDC) and Bitcoin (BTC). These assets are owned and managed solely by Thunderbolt for corporate purposes.',
    items: [
      { label: 'No direct user ownership', text: 'Users do not have direct ownership of treasury assets. Reward allocations represent a beneficial interest in BTC held by Thunderbolt, subject to these Terms.' },
      { label: 'No custody relationship', text: 'Thunderbolt is not a licensed custodian. The existence of a treasury involving digital assets does not create a formal custody relationship or regulated financial obligation to users beyond what is stated in these Terms.' },
      { label: 'USD-equivalent display', text: 'Earning balances displayed within the Services represent a USD-equivalent value calculated using live Bitcoin market prices. The actual allocated asset is Bitcoin.' },
    ]
  },
  {
    number: '10',
    title: 'No Cryptocurrency Exchange Services',
    content: "Thunderbolt does not provide cryptocurrency exchange, brokerage, or wallet services to users. Users do not buy, sell, or trade cryptocurrency directly through the Services. Thunderbolt's acquisition and management of Bitcoin is conducted internally for treasury and rewards purposes only.",
    items: [
      { label: 'No crypto deposits', text: 'Users do not deposit cryptocurrency into Thunderbolt.' },
      { label: 'No crypto withdrawals', text: 'Users do not withdraw cryptocurrency directly from Thunderbolt.' },
      { label: 'No trading', text: 'Users do not buy or sell cryptocurrency through the Services.' },
    ]
  },
  {
    number: '11',
    title: 'Risk Disclosure',
    content: 'Bitcoin is a highly speculative and volatile asset. Its price can increase or decrease dramatically within short periods of time, including losing all value. By participating in the Earning rewards program, you acknowledge and accept that:',
    items: [
      { label: 'Value fluctuation', text: 'The USD-equivalent value of your Earning balance can go up or down at any time.' },
      { label: 'No guarantee', text: 'You may redeem significantly less than the value shown at any given moment.' },
      { label: 'No guaranteed return', text: 'Thunderbolt does not guarantee any specific return, value, or redemption amount.' },
      { label: 'Past performance', text: 'Past performance of Bitcoin is not indicative of future results.' },
      { label: 'Balance risk', text: 'Your Available Balance is not insured and represents a claim against Thunderbolt, not a bank deposit.' },
    ],
    footer: 'Use of electronic payment systems also involves inherent risks, including transaction delays, processor errors, and network outages. Thunderbolt is not liable for losses resulting from third-party processor actions, Bitcoin price movements, or service interruptions.'
  },
  {
    number: '12',
    title: 'Prohibited Businesses & Uses',
    content: 'To maintain our integration with global financial partners, Thunderbolt strictly prohibits use by businesses or individuals involved in the following categories. Thunderbolt employs automated content monitoring that scans payment descriptions and merchant names for prohibited terms. Requests containing flagged terms are automatically blocked or escalated for manual review. Repeated violations may result in immediate account suspension. Thunderbolt reserves the right to update its list of monitored and prohibited terms at any time without notice.',
    items: [
      { label: 'Illegal Activity', text: 'Narcotics, controlled substances, or drug paraphernalia.' },
      { label: 'Adult Content', text: 'Pornography, escort services, or sexually explicit content.' },
      { label: 'Regulated Goods', text: 'Unauthorized sale of tobacco, firearms, or prescription drugs.' },
      { label: 'Financial Schemes', text: 'Multi-level marketing (MLM), pyramid schemes, or high-yield investment programs.' },
      { label: 'Gambling', text: 'Online casinos, sports betting, or unlicensed games of skill.' },
      { label: 'Fraud', text: 'Money laundering, identity theft, or unauthorized transactions.' },
    ],
    footerText: 'Thunderbolt reserves the right to terminate any account found to be in violation of these prohibitions or the ',
    footerLink: { text: 'Coinbase Prohibited Use Policy', url: 'https://www.coinbase.com/legal/prohibited_use' },
    footerSuffix: '.'
  },
  {
    number: '13',
    title: 'Acceptable Use',
    content: 'You agree not to use the Services to engage in illegal activity, commit fraud or unauthorized transactions, interfere with platform security or operations, or circumvent payment processing systems. We reserve the right to restrict access for violations.'
  },
  {
    number: '14',
    title: 'Tax Responsibility',
    content: 'Thunderbolt may provide transaction summaries for informational purposes only. You are solely responsible for determining the tax treatment of any payments received or rewards earned, including any tax obligations arising from Bitcoin appreciation or redemption. Thunderbolt does not provide tax advice.'
  },
  {
    number: '15',
    title: 'Third-Party Services',
    content: 'The Services rely on third-party providers, including payment processors and infrastructure providers. We are not responsible for third-party services, availability, or performance.'
  },
  {
    number: '16',
    title: 'Limitation of Liability',
    content: 'The Services are provided "as is." To the maximum extent permitted by law, Thunderbolt shall not be liable for indirect or consequential damages, loss of profits or revenue, payment processing failures, or losses resulting from Bitcoin price volatility. Our total liability shall not exceed fees paid to us in the prior three (3) months.'
  },
  {
    number: '17',
    title: 'Termination',
    content: 'We may suspend or terminate access to the Services at any time, with or without notice, for violations of these Terms, fraudulent activity, or legal or regulatory requirements.'
  },
  {
    number: '18',
    title: 'Changes to Terms',
    content: 'We may update these Terms from time to time. Continued use of the Services after changes are posted constitutes acceptance of the updated Terms.'
  },
  {
    number: '19',
    title: 'Governing Law',
    content: 'These Terms shall be governed by and interpreted under the laws of the United States, without regard to conflict of law principles.'
  },
];

export default function TermsPage() {
  useEffect(() => {
    document.title = 'Thunderbolt — Terms of Service';
    return () => { document.title = 'Thunderbolt'; };
  }, []);

  const navigate = useNavigate();
  const [limits, setLimits] = useState({
    sendWeekly: '4,999.99',
    cashoutSingle: '10,000.00',
    cashoutWeekly: '49,999.99',
    guestDaily: '10,000.00',
    guestWeekly: '49,999.00',
  });
  const [feePct, setFeePct] = useState("0.50");

  useEffect(() => {
    fetch((process.env.REACT_APP_API_URL || '') + '/platform-settings')
      .then(r => r.json())
      .then(data => {
        const fmt = cents => (parseInt(cents||0)/100).toLocaleString('en-US', { minimumFractionDigits:2, maximumFractionDigits:2 });
        setLimits({
          sendWeekly:    fmt(data.send_weekly_limit_cents    || 499999),
          cashoutSingle: fmt(data.cashout_single_limit_cents || 1000000),
          cashoutWeekly: fmt(data.cashout_weekly_limit_cents || 4999999),
          guestDaily:    fmt(data.guest_daily_limit_cents    || 1000000),
          guestWeekly:   fmt(data.guest_weekly_limit_cents   || 4999900),
        });
        setFeePct(data.feePct || "0.50");
      })
      .catch(() => {});
  }, []);

  return (
    <div style={s.page}>
      <div style={s.header}>
        <button style={s.back} onClick={() => navigate(-1)}>
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#1D1D1F" strokeWidth="2">
            <path d="M19 12H5M12 5l-7 7 7 7"/>
          </svg>
        </button>
        <span style={s.headerTitle}>Terms of Service</span>
        <div style={{width:36}}/>
      </div>

      <div style={s.body}>
        <div style={s.updated}>Effective Date: May 3, 2026</div>
        <p style={s.intro}>
          These Terms of Service govern your access to and use of Thunderbolt, including our website, applications, payment tools, and related services. By using the Services, you agree to these Terms.
        </p>

        {/* Dynamic Transaction Limits Section */}
        {sections.slice(0, 7).map(sec => (
          <div key={sec.number} style={s.section}>
            <div style={s.sectionHeader}>
              <div style={s.sectionNumber}>{sec.number}</div>
              <div style={s.sectionTitle}>{sec.title}</div>
            </div>
            {sec.content && <p style={s.text}>{sec.content}</p>}
            {sec.title === 'Fees' && <p style={s.text}>Thunderbolt currently charges a <strong>{feePct}%</strong> service fee on each transaction. This rate is subject to change; the current rate is always reflected here.</p>}
            {sec.items && (
              <div style={s.itemList}>
                {sec.items.map((item, i) => (
                  <div key={i} style={s.item}>
                    <div style={s.itemLabel}>{item.label}</div>
                    <div style={s.itemText}>{item.text}</div>
                  </div>
                ))}
              </div>
            )}
            {sec.footer && <p style={{...s.text, marginTop:12, fontStyle:'italic'}}>{sec.footer}</p>}
            {sec.footerText && (
              <p style={{...s.text, marginTop:12}}>
                {sec.footerText}
                <a href={sec.footerLink.url} target="_blank" rel="noreferrer" style={s.link}>{sec.footerLink.text}</a>
                {sec.footerSuffix}
              </p>
            )}
          </div>
        ))}

        {/* Transaction Limits — live from platform settings */}
        <div style={s.section}>
          <div style={s.sectionHeader}>
            <div style={s.sectionNumber}>7A</div>
            <div style={s.sectionTitle}>Transaction Limits</div>
          </div>
          <p style={s.text}>To maintain platform integrity and comply with applicable regulations, Thunderbolt enforces the following transaction limits. These limits are subject to change and the most current limits are reflected here.</p>
          <div style={s.itemList}>
            <div style={s.item}>
              <div style={s.itemLabel}>Person-to-Person Transfers</div>
              <div style={s.itemText}>Up to <strong>${limits.sendWeekly}</strong> per week per user.</div>
            </div>
            <div style={s.item}>
              <div style={s.itemLabel}>Bank Cashout — Single Transaction</div>
              <div style={s.itemText}>Up to <strong>${limits.cashoutSingle}</strong> per transaction.</div>
            </div>
            <div style={s.item}>
              <div style={s.itemLabel}>Bank Cashout — Weekly</div>
              <div style={s.itemText}>Up to <strong>${limits.cashoutWeekly}</strong> per week per user.</div>
            </div>
            <div style={s.item}>
              <div style={s.itemLabel}>Guest Payments — Daily</div>
              <div style={s.itemText}>Up to <strong>${limits.guestDaily}</strong> per day per payer.</div>
            </div>
            <div style={s.item}>
              <div style={s.itemLabel}>Guest Payments — Weekly</div>
              <div style={s.itemText}>Up to <strong>${limits.guestWeekly}</strong> per week per payer.</div>
            </div>
          </div>
          <p style={{...s.text, marginTop:12, fontStyle:'italic'}}>Thunderbolt reserves the right to modify these limits at any time. Current limits are always reflected in this Terms of Service.</p>
        </div>

        {sections.slice(7).map(sec => (
          <div key={sec.number} style={s.section}>
            <div style={s.sectionHeader}>
              <div style={s.sectionNumber}>{sec.number}</div>
              <div style={s.sectionTitle}>{sec.title}</div>
            </div>
            {sec.content && <p style={s.text}>{sec.content}</p>}
            {sec.title === 'Fees' && <p style={s.text}>Thunderbolt currently charges a <strong>{feePct}%</strong> service fee on each transaction. This rate is subject to change; the current rate is always reflected here.</p>}
            {sec.items && (
              <div style={s.itemList}>
                {sec.items.map((item, i) => (
                  <div key={i} style={s.item}>
                    <div style={s.itemLabel}>{item.label}</div>
                    <div style={s.itemText}>{item.text}</div>
                  </div>
                ))}
              </div>
            )}
            {sec.footer && (
              <p style={{...s.text, marginTop:12, fontStyle:'italic'}}>{sec.footer}</p>
            )}
            {sec.footerText && (
              <p style={{...s.text, marginTop:12}}>
                {sec.footerText}
                <a href={sec.footerLink.url} target="_blank" rel="noreferrer" style={s.link}>
                  {sec.footerLink.text}
                </a>
                {sec.footerSuffix}
              </p>
            )}
          </div>
        ))}

        {/* Transaction Limits — live from platform settings */}
        <div style={s.section}>
          <div style={s.sectionHeader}>
            <div style={s.sectionNumber}>7A</div>
            <div style={s.sectionTitle}>Transaction Limits</div>
          </div>
          <p style={s.text}>To maintain platform integrity and comply with applicable regulations, Thunderbolt enforces the following transaction limits. These limits are subject to change and the most current limits are reflected here.</p>
          <div style={s.itemList}>
            <div style={s.item}><div style={s.itemLabel}>Person-to-Person Transfers</div><div style={s.itemText}>Up to <strong>${limits.sendWeekly}</strong> per week per user.</div></div>
            <div style={s.item}><div style={s.itemLabel}>Bank Cashout — Single Transaction</div><div style={s.itemText}>Up to <strong>${limits.cashoutSingle}</strong> per transaction.</div></div>
            <div style={s.item}><div style={s.itemLabel}>Bank Cashout — Weekly</div><div style={s.itemText}>Up to <strong>${limits.cashoutWeekly}</strong> per week per user.</div></div>
            <div style={s.item}><div style={s.itemLabel}>Guest Payments — Daily</div><div style={s.itemText}>Up to <strong>${limits.guestDaily}</strong> per day per payer.</div></div>
            <div style={s.item}><div style={s.itemLabel}>Guest Payments — Weekly</div><div style={s.itemText}>Up to <strong>${limits.guestWeekly}</strong> per week per payer.</div></div>
          </div>
          <p style={{...s.text, marginTop:12, fontStyle:'italic'}}>Thunderbolt reserves the right to modify these limits at any time. Current limits are always reflected in this Terms of Service.</p>
        </div>

        {sections.slice(7).map(sec => (
          <div key={sec.number} style={s.section}>
            <div style={s.sectionHeader}>
              <div style={s.sectionNumber}>{sec.number}</div>
              <div style={s.sectionTitle}>{sec.title}</div>
            </div>
            {sec.content && <p style={s.text}>{sec.content}</p>}
            {sec.title === 'Fees' && <p style={s.text}>Thunderbolt currently charges a <strong>{feePct}%</strong> service fee on each transaction. This rate is subject to change; the current rate is always reflected here.</p>}
            {sec.items && (
              <div style={s.itemList}>
                {sec.items.map((item, i) => (
                  <div key={i} style={s.item}>
                    <div style={s.itemLabel}>{item.label}</div>
                    <div style={s.itemText}>{item.text}</div>
                  </div>
                ))}
              </div>
            )}
            {sec.footer && <p style={{...s.text, marginTop:12, fontStyle:'italic'}}>{sec.footer}</p>}
            {sec.footerText && (
              <p style={{...s.text, marginTop:12}}>
                {sec.footerText}
                <a href={sec.footerLink.url} target="_blank" rel="noreferrer" style={s.link}>{sec.footerLink.text}</a>
                {sec.footerSuffix}
              </p>
            )}
          </div>
        ))}

        <div style={s.contact}>
          <div style={s.contactTitle}>Questions?</div>
          <div style={s.contactText}>Contact us at</div>
          <a href="mailto:support@thunderboltapps.com" style={s.link}>
            support@thunderboltapps.com
          </a>
        </div>
      </div>
    </div>
  );
}

const s = {
  page:          {minHeight:'100vh', background:'#F5F5F7', paddingBottom:40},
  header:        {display:'flex', alignItems:'center', justifyContent:'space-between', padding:'56px 20px 16px'},
  back:          {width:36, height:36, borderRadius:10, background:'#fff', border:'none', display:'flex', alignItems:'center', justifyContent:'center', boxShadow:'0 2px 8px rgba(0,0,0,0.08)', cursor:'pointer'},
  headerTitle:   {fontSize:17, fontWeight:600, color:'#1D1D1F'},
  body:          {padding:'0 32px 32px', maxWidth:'100%', margin:'0 auto', width:'100%'},
  updated:       {fontSize:12, color:'#AEAEB2', marginBottom:12, textAlign:'center'},
  intro:         {fontSize:14, color:'#6E6E73', lineHeight:1.7, marginBottom:20, textAlign:'center'},
  section:       {background:'#fff', borderRadius:16, padding:20, marginBottom:12, boxShadow:'0 2px 20px rgba(0,0,0,0.06)'},
  sectionHeader: {display:'flex', alignItems:'center', gap:12, marginBottom:12},
  sectionNumber: {width:28, height:28, borderRadius:8, background:'#007AFF', color:'#fff', display:'flex', alignItems:'center', justifyContent:'center', fontSize:13, fontWeight:700, flexShrink:0},
  sectionTitle:  {fontSize:15, fontWeight:600, color:'#1D1D1F'},
  text:          {fontSize:14, color:'#6E6E73', lineHeight:1.7, margin:0},
  itemList:      {display:'flex', flexDirection:'column', gap:8, marginTop:8},
  item:          {background:'#F5F5F7', borderRadius:10, padding:'12px 14px'},
  itemLabel:     {fontSize:13, fontWeight:600, color:'#1D1D1F', marginBottom:4},
  itemText:      {fontSize:13, color:'#6E6E73', lineHeight:1.6},
  link:          {color:'#007AFF', textDecoration:'none'},
  contact:       {background:'#EBF4FF', borderRadius:16, padding:20, textAlign:'center', marginTop:8},
  contactTitle:  {fontSize:15, fontWeight:600, color:'#1D1D1F', marginBottom:4},
  contactText:   {fontSize:13, color:'#6E6E73', marginBottom:8},
};
